/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
// @sfdc-extension-line SFDC_EXT_BOPIS
import { useShowPickupAvailable } from './use-pickup-filter';
import { useProductGridSurface, type ProductGridSurface } from './use-grid-surface';
import type { ShopperSearch } from '@/scapi';
import DynamicImageProvider from '@/providers/dynamic-image';
import { ProductTile, ProductTileProvider } from '@/components/product-tile';
import { ProductTileSkeleton } from '@/components/category-skeleton';
import { Skeleton } from '@/components/ui/skeleton';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];

// Responsive size of the product images in the product grid when the refinement panel is visible.
// Values are based on the grid column configuration and refinement panel width
// (w-64 + gap-8 --> 256px + 32px = 288px).
const responsiveImageWidthsWithRefinements = [
    '45vw', // base: 2 grid columns, refinement panel stacks above the grid
    '45vw', // sm:   2 grid columns, refinement panel stacks above the grid
    '22vw', // md:   3 grid columns, refinement panel beside the grid ≈ (100vw − 288px) / 3
    '18vw', // lg:   4 grid columns, refinement panel beside the grid ≈ (100vw − 288px) / 4
    '18vw', // xl:   4 grid columns, refinement panel beside the grid
    '18vw', // 2xl:  4 grid columns, refinement panel beside the grid
];

// Responsive size of product images when refinements panel is collapsed.
const responsiveImageWidthsWithoutRefinements = [
    '45vw', // base: 2 grid columns, no refinement panel ≈ (100vw − col padding) / 2
    '45vw', // sm:   2 grid columns, no refinement panel
    '30vw', // md:   3 grid columns, no refinement panel ≈ (100vw − col padding) / 3
    '23vw', // lg:   4 grid columns, no refinement panel ≈ (100vw − col padding) / 4
    '22vw', // xl:   4 grid columns, no refinement panel
    '22vw', // 2xl:  4 grid columns, no refinement panel
];

function NoProductsMessage({
    criticalSize,
    nonCriticalSize,
    emptyState,
}: {
    criticalSize: number;
    nonCriticalSize: number;
    emptyState?: ReactNode;
}) {
    const { t } = useTranslation('common');

    if (criticalSize > 0 || nonCriticalSize > 0) {
        return null;
    }
    return (
        <div className="col-span-full" data-testid="product-grid-empty">
            {emptyState ?? <p className="type-body py-12 text-center text-muted-foreground">{t('noProductsFound')}</p>}
        </div>
    );
}

function NonCriticalContent({
    products,
    criticalSize,
    responsiveImageWidths,
    handleProductClick,
    topCategoryName,
    emptyState,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable,
}: {
    products: ProductSearchHit[];
    criticalSize: number;
    responsiveImageWidths: string[];
    handleProductClick?: (product: ProductSearchHit) => void;
    topCategoryName?: string;
    emptyState?: ReactNode;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
}) {
    return (
        <DynamicImageProvider value={{ widths: responsiveImageWidths }}>
            {products.map((product) => (
                <ProductTile
                    key={product.productId}
                    product={product}
                    handleProductClick={handleProductClick}
                    showNavigationArrows
                    topCategoryName={topCategoryName}
                    // @sfdc-extension-line SFDC_EXT_BOPIS
                    showPickupAvailable={showPickupAvailable}
                />
            ))}
            <NoProductsMessage criticalSize={criticalSize} nonCriticalSize={products.length} emptyState={emptyState} />
        </DynamicImageProvider>
    );
}

function ProductGridSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div className="srp-grid">
            {Array.from({ length: count }, (_, index) => (
                <div key={`grid-skeleton-${index}`} className="space-y-3">
                    <Skeleton className="aspect-square w-full" />
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-2/3" />
                        <Skeleton className="h-5 w-20" />
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * ProductGrid renders product tiles in a responsive grid layout, wrapping all tiles in a shared
 * context provider to reduce hydration overhead. Instead of each tile initializing its own hooks
 * (navigate, config, translation, currency), the provider initializes them once and shares them
 * via context.
 *
 * The grid accepts synchronous data for both critical (above-the-fold) and non-critical
 * (below-the-fold) products. Critical product images are loaded with high priority and eagerly.
 * For deferred loading of non-critical products, use DeferredProductGrid.
 */
export default function ProductGrid({
    critical,
    nonCritical,
    hasRefinementsPanel = true,
    handleProductClick,
    topCategoryName,
    isLoading = false,
    skeletonCount,
    emptyState,
    surface: surfaceProp,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable: showPickupAvailableProp,
}: {
    critical?: ProductSearchHit[];
    nonCritical?: ProductSearchHit[];
    hasRefinementsPanel?: boolean;
    handleProductClick?: (product: ProductSearchHit) => void;
    topCategoryName?: string;
    isLoading?: boolean;
    skeletonCount?: number;
    /** Surface-specific empty state rendered when the result set is empty. */
    emptyState?: ReactNode;
    /**
     * WHICH surface these tiles are standing on. Not cosmetic: the tile's hover
     * action row is keyed by (brand, surface) in `lib/brand/overlay-model.ts`,
     * and live BG has a `View Similar` bar on search tiles and none on PLP tiles.
     * A grid that cannot tell the two apart paints BG's search bar on its PLP.
     *
     * Optional because neither route passes it yet; omitted, it is derived from
     * the matched route (`./use-grid-surface`). Passing it explicitly — the way
     * both routes already pass `surface` to `SearchStickyBar` — is preferred and
     * always wins.
     */
    surface?: ProductGridSurface;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
}): ReactElement {
    const criticalData = critical ?? [];
    const l = criticalData.length;
    const responsiveImageWidths = hasRefinementsPanel
        ? responsiveImageWidthsWithRefinements
        : responsiveImageWidthsWithoutRefinements;

    // Initialize the `<DynamicImageProvider/>` behavior for the scope of this grid.
    // Out-of-the-box we make sure that the product images of all products considered critical (displayed inside a
    // `<DynamicImage/>` component) should be loaded eagerly with high priority.
    const hasSource = useCallback(() => true, []);

    // An explicit prop always wins; the route-derived value is the fallback for
    // callers that have not declared one. Both branches resolve to a real
    // `TileSurface`, so no tile ever falls through to a guess.
    const routeSurface = useProductGridSurface();
    const surface = surfaceProp ?? routeSurface;

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const pickupFromUrl = useShowPickupAvailable();
    const showPickupAvailable = showPickupAvailableProp ?? pickupFromUrl;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS
    const loadingSkeletonCount = Math.max(criticalData.length + (nonCritical?.length ?? 0), 4);

    if (isLoading) {
        return (
            <ProductTileProvider surface={surface}>
                <div data-testid="product-grid-loading-state" aria-busy>
                    <ProductGridSkeleton count={loadingSkeletonCount} />
                </div>
            </ProductTileProvider>
        );
    }

    return (
        <ProductTileProvider surface={surface}>
            {/* NM column steps (docs/archetypes/plp.md — confirmed production):
                2 columns at mobile, 4 at desktop. The tablet band steps through 3
                so tiles stay legible next to the 256px refinements rail at `md`.
                Gutters/row rhythm are the --tile-* tokens (blast-4: ~20px
                effective gutters — live's 10+10 tile pads as a plain gap — and
                24px tile margin-bottom); BG pins its previous 16/32 gaps. */}
            <div data-testid="product-grid-list" className="srp-grid">
                {l > 0 && (
                    <DynamicImageProvider value={{ hasSource, widths: responsiveImageWidths }}>
                        {criticalData.map((product) => (
                            <ProductTile
                                key={product.productId}
                                product={product}
                                handleProductClick={handleProductClick}
                                showNavigationArrows
                                topCategoryName={topCategoryName}
                                // @sfdc-extension-line SFDC_EXT_BOPIS
                                showPickupAvailable={showPickupAvailable}
                            />
                        ))}
                    </DynamicImageProvider>
                )}
                {nonCritical && nonCritical.length > 0 && (
                    <NonCriticalContent
                        products={nonCritical}
                        criticalSize={l}
                        responsiveImageWidths={responsiveImageWidths}
                        handleProductClick={handleProductClick}
                        topCategoryName={topCategoryName}
                        emptyState={emptyState}
                        // @sfdc-extension-line SFDC_EXT_BOPIS
                        showPickupAvailable={showPickupAvailable}
                    />
                )}
                {(skeletonCount ?? 0) > 0 &&
                    Array.from({ length: skeletonCount ?? 0 }, (_, i) => <ProductTileSkeleton key={`skeleton-${i}`} />)}
                {!skeletonCount && (
                    <NoProductsMessage
                        criticalSize={l}
                        nonCriticalSize={nonCritical?.length ?? 0}
                        emptyState={emptyState}
                    />
                )}
            </div>
        </ProductTileProvider>
    );
}
